import React, { useEffect, useRef, useState } from 'react';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/images/login-logo.png';
import brand from '../assets/images/GRACESUPERMARKET.png';
import forgotImage from '../assets/images/forgetpassword.png';
import otpImage from '../assets/images/otp-verify.svg';
import confirmImage from '../assets/images/confirmpassword.png';
import successImage from '../assets/images/password-success-bg.svg';
import { API_BASE_URL } from '../utils/api';
import './PasswordResetFlow.css';

const OTP_LENGTH = 6;

const PasswordResetFlow = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(''));
  const otpInputsRef = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    setError('');

    if (step === 2) {
      focusOtpInput(0);
    }

    if (step === 3) {
      setNewPassword('');
      setConfirmPassword('');
    }
  }, [step]);

  const handleBack = () => {
    if (step === 1) {
      navigate('/login');
    } else if (step === 4) {
      navigate('/login', { replace: true });
    } else {
      setMessage('');
      setError('');
      if (step === 3) {
        setResetToken('');
      }
      setStep((prev) => Math.max(1, prev - 1));
    }
  };

  const handleRequestOtp = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Please enter your registered email address.');
      return;
    }

    const isEmail = /.+@.+\..+/.test(trimmedEmail);

    if (!isEmail) {
      setError('Please provide the email address associated with your admin account.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ usernameOrEmail: trimmedEmail }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.message || 'Unable to send OTP. Please try again.');
      }

      setMessage(result?.message || 'OTP has been sent to your registered email address.');
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setResetToken('');
      setStep(2);
    } catch (err) {
      setError(err.message || 'Unable to send OTP. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const focusOtpInput = (index) => {
    const input = otpInputsRef.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/\d?/.test(value)) {
      return;
    }

    const digits = [...otpDigits];
    digits[index] = value.slice(-1);
    setOtpDigits(digits);

    if (value && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1);
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === 'Backspace' && !otpDigits[index] && index > 0) {
      focusOtpInput(index - 1);
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) {
      return;
    }

    const digits = text.split('');
    const newDigits = Array(OTP_LENGTH)
      .fill('')
      .map((_, idx) => digits[idx] || '');
    setOtpDigits(newDigits);

    const lastFilledIndex = Math.min(text.length, OTP_LENGTH) - 1;
    focusOtpInput(lastFilledIndex >= 0 ? lastFilledIndex : 0);
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const otpValue = otpDigits.join('');

    if (!trimmedEmail || otpValue.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit OTP sent to your email.`);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: trimmedEmail, otp: otpValue }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result?.resetToken) {
        throw new Error(result?.message || 'Invalid OTP. Please try again.');
      }

      setMessage('OTP verified successfully. You can now change your password.');
      setResetToken(result.resetToken);
      setStep(3);
    } catch (err) {
      setError(err.message || 'Unable to verify the OTP. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    const trimmedPassword = newPassword.trim();
    const trimmedConfirm = confirmPassword.trim();

    if (!trimmedPassword || !trimmedConfirm) {
      setError('Please fill in both password fields.');
      return;
    }

    if (trimmedPassword.length < 8) {
      setError('Password should be at least 8 characters long.');
      return;
    }

    if (trimmedPassword !== trimmedConfirm) {
      setError('Passwords do not match.');
      return;
    }

    if (!resetToken) {
      setError('Reset token missing. Please verify the OTP again.');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ resetToken, newPassword: trimmedPassword }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.message || 'Unable to reset the password. Please try again.');
      }

      setMessage(result?.message || 'Password updated successfully.');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setStep(4);
    } catch (err) {
      setError(err.message || 'Unable to reset the password. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <form onSubmit={handleRequestOtp} className="auth-card">
          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}
          <div className="mb-4">
            <label className="form-label fw-semibold">Enter E-Mail Address</label>
            <input
              type="email"
              className="form-control"
              placeholder="name@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="email"
              disabled={isSubmitting}
            />
          </div>
          <button type="submit" className="btn btn-danger w-100" disabled={isSubmitting}>
            {isSubmitting ? 'Sending OTP...' : 'Send OTP'}
          </button>
        </form>
      );
    }

    if (step === 2) {
      return (
        <form onSubmit={handleVerifyOtp} className="auth-card">
          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}
          <p className="text-muted small mb-4">
            We have sent a {OTP_LENGTH}-digit code to <strong>{email}</strong>. Enter the code below to proceed.
          </p>
          <div className="otp-inputs mb-4" onPaste={handleOtpPaste}>
            {otpDigits.map((digit, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(event) => handleOtpChange(index, event.target.value)}
                onKeyDown={(event) => handleOtpKeyDown(index, event)}
                ref={(element) => {
                  otpInputsRef.current[index] = element;
                }}
                disabled={isSubmitting}
              />
            ))}
          </div>
          <button type="submit" className="btn btn-danger w-100" disabled={isSubmitting}>
            {isSubmitting ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      );
    }

    if (step === 3) {
      return (
        <form onSubmit={handleResetPassword} className="auth-card">
          {error && <div className="alert alert-danger">{error}</div>}
          {message && <div className="alert alert-success">{message}</div>}
          <div className="mb-4">
            <label className="form-label fw-semibold">Enter New Password</label>
            <div className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-control"
                placeholder="Password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            <small className="text-muted">Passwords should be at least 8 characters long, and longer is better.</small>
          </div>
          <div className="mb-4">
            <label className="form-label fw-semibold">Confirm New Password</label>
            <div className="password-field">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                className="form-control"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>
          <button type="submit" className="btn btn-danger w-100" disabled={isSubmitting}>
            {isSubmitting ? 'Updating Password...' : 'Change Password'}
          </button>
        </form>
      );
    }

    return (
      <div className="reset-success" style={{ backgroundImage: `url(${successImage})` }}>
        <div className="success-modal">
          <div className="display-4 mb-3" role="img" aria-label="Confetti">
            🎉
          </div>
          <h3>Password Updated Successfully</h3>
          <p>Your password has been changed. You can now log back into the portal with your new credentials.</p>
          <button type="button" className="btn btn-danger" onClick={handleGoToLogin}>
            Back to Login
          </button>
        </div>
      </div>
    );
  };

  const getCurrentImage = () => {
    if (step === 1) return forgotImage;
    if (step === 2) return otpImage;
    if (step === 3) return confirmImage;
    return successImage;
  };

  const renderHeader = () => (
    <>
      <a href="#back" className="auth-back-button" onClick={(event) => { event.preventDefault(); handleBack(); }}>
        <FaArrowLeft aria-hidden="true" /> Back
      </a>
      <div className="text-center auth-header">
        <img src={logo} alt="Grace Super Market" className="mb-3" />
        <img src={brand} alt="GRACE SUPER MARKET" className="mb-3" />
        <p className="mb-4">Human Resource Management System</p>
        {step === 1 && (
          <>
            <h5 className="fw-bold mb-3">Forgot Password</h5>
            <p className="text-muted small mb-0 text-start">
              Enter your registered email address. We&apos;ll send you a code to reset your password.
            </p>
          </>
        )}
        {step === 2 && (
          <>
            <h5 className="fw-bold mb-3">Enter OTP</h5>
            <p className="text-muted small mb-0 text-start">
              Enter the one-time passcode we sent to your email address to continue.
            </p>
          </>
        )}
        {step === 3 && (
          <>
            <h5 className="fw-bold mb-3">Enter New Password</h5>
            <p className="text-muted small mb-0 text-start">
              Create a strong password that you haven&apos;t used for this account.
            </p>
          </>
        )}
      </div>
    </>
  );

  if (step === 4) {
    return renderStepContent();
  }

  return (
    <div className="container-fluid p-0 auth-layout">
      <div className="row g-0">
        <div className="col-lg-6 d-none d-lg-block auth-image-col">
          <img src={getCurrentImage()} alt="Password reset" />
        </div>
        <div className="col-lg-6 d-flex align-items-center">
          <div className="auth-content">
            {renderHeader()}
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetFlow;
